import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Modal, notification, Typography } from "antd";
import {
  CERTIFICATE_OF_ORIGIN_FEE_DELETE_ENDPOINT,
  CERTIFICATE_OF_ORIGIN_FEE_LIST_ENDPOINT,
} from "apiServices/API_ENDPOINTS";
import { deleteData, getData } from "apiServices/common";
import AppContent from "common/AppContent";
import ListTable from "common/ListTable";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CERTIFICATE_OF_ORIGIN_FEE_CREATE_PERMISSION,
  CERTIFICATE_OF_ORIGIN_FEE_DELETE_PERMISSION,
  CERTIFICATE_OF_ORIGIN_FEE_EDIT_PERMISSION,
  CERTIFICATE_OF_ORIGIN_FEE_VIEW_PERMISSION,
} from "routes/permissions";
import { hasPermission } from "utils/functions";

export default function CertificateOfOriginFee() {
  // Router
  const location = useLocation();
  const navigate = useNavigate();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Antd
  const { Title } = Typography;

  // Redux
  const filterData = useSelector(({ filter }) => filter.filterData);

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      localStorage.setItem(
        "filter_status",
        JSON.stringify({
          ...parsedFilterStatus,
          [routerPath]: true,
        }),
      );
    } else {
      localStorage.setItem(
        "filter_status",
        JSON.stringify({
          ...parsedFilterStatus,
          [routerPath]: false,
        }),
      );
    }
  }, [showFilter, routerPath]);

  const getAllExportDocFees = useCallback(
    async (filterValues) => {
      const {
        sales_contract_id,
        invoice_id,
        invoice_number,
        sales_contract_number,
      } = filterValues || {};

      setLoading(true);
      setFetchError(null);

      const query = `${CERTIFICATE_OF_ORIGIN_FEE_LIST_ENDPOINT}?page=${currentPage}`;
      const bodyData = {
        ...(sales_contract_id && { sales_contract_id }),
        ...(invoice_id && { invoice_id }),
        ...(invoice_number && { invoice_number }),
        ...(sales_contract_number && { sales_contract_number }),
      };

      console.log("Filter data being sent to API:", bodyData);

      const response = await getData(query, false, bodyData);
      if (response?.status === 200) {
        const data = response.data?.data?.data || [];
        // Add unique key if id is missing
        const dataWithKeys = data.map((item, index) => ({
          ...item,
          key: item.id || `certificate-of-origin-fee-${index}`,
        }));

        setTableData(dataWithKeys);
        setMetaData({
          current: response.data?.data?.current_page || 1,
          defaultCurrent: response?.data?.data?.from || 1,
          defaultPageSize: response?.data?.data?.per_page || 10,
          total: response.data?.data?.total || 0,
        });
        setLoading(false);
      } else {
        setTableData([]);
        setLoading(false);
        setFetchError("Could not load this list.");
        notification.error({
          message: "Error",
          description: "Failed to load Certificate of Origin Fee data",
          placement: "topRight",
        });
      }
    },
    [currentPage],
  );

  // Effects
  useEffect(() => {
    getAllExportDocFees(filterData);
  }, [refresh, filterData, getAllExportDocFees]);

  const refetch = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);

  const deleteSelectedExportDocFee = useCallback(
    async (id) => {
      Modal.confirm({
        title: "Confirm Deletion",
        content:
          "Are you sure you want to delete this Certificate of Origin Fee?",
        okText: "Yes, Delete",
        okType: "danger",
        cancelText: "Cancel",
        onOk: async () => {
          setLoading(true);
          const response = await deleteData(
            `${CERTIFICATE_OF_ORIGIN_FEE_DELETE_ENDPOINT}/${id}`,
          );

          if (
            response &&
            (response?.code === 200 || response?.status === 200)
          ) {
            setLoading(false);
            notification.success({
              message: "Success",
              description: "Certificate of Origin Fee deleted successfully",
              placement: "topRight",
            });
            refetch();
          } else {
            setLoading(false);
            notification.error({
              message: "Error",
              description:
                response?.message || "Failed to delete. Please try again.",
              placement: "topRight",
            });
          }
        },
        onCancel: () => {
          setLoading(false);
        },
      });
    },
    [refetch],
  );

  const navigateToAdd = useCallback(() => {
    navigate("/import-export-expense-tracking/certificate-of-origin-fee/add");
  }, [navigate]);

  const navigateToEdit = useCallback(
    (record) => {
      navigate(
        `/import-export-expense-tracking/certificate-of-origin-fee/edit/${record.id}`,
      );
    },
    [navigate],
  );

  const navigateToView = useCallback(
    (record) => {
      navigate(
        `/import-export-expense-tracking/certificate-of-origin-fee/view/${record.id}`,
      );
    },
    [navigate],
  );

  const changePage = (newPage) => {
    setCurrentPage(newPage);
  };

  const tableColumns = useMemo(
    () => [
      {
        title: "Invoice Number",
        dataIndex: ["export_details", "invoice_number"],
        width: "20%",
        key: "invoiceNumber",
        ellipsis: true,
        render: (invoiceNumber) => invoiceNumber || "N/A",
      },
      {
        title: "Sales Contract No",
        dataIndex: ["export_details", "sales_contract_info", "reference_no"],
        width: "20%",
        key: "salesContractNumber",
        ellipsis: true,
        render: (refNo) => refNo || "N/A",
      },
      {
        title: "Total Value",
        dataIndex: "total",
        width: "15%",
        key: "totalValue",
        ellipsis: true,
        render: (total) => (total ? `${total} BDT` : "N/A"),
      },
      {
        title: "TRN Date",
        dataIndex: "trn_date",
        width: "15%",
        key: "trnDate",
        ellipsis: true,
        render: (date) => date || "N/A",
      },
      {
        title: "Action",
        key: "action",
        width: "10%",
        render: (_, record) => {
          const menuItems = [
            {
              key: "view",
              label: "View",
              icon: <EyeOutlined />,
              onClick: () => navigateToView(record),
              disabled: !hasPermission([
                CERTIFICATE_OF_ORIGIN_FEE_VIEW_PERMISSION,
              ]),
            },
            {
              key: "edit",
              label: "Edit",
              icon: <EditOutlined />,
              onClick: () => navigateToEdit(record),
              disabled: !hasPermission([
                CERTIFICATE_OF_ORIGIN_FEE_EDIT_PERMISSION,
              ]),
            },
            {
              type: "divider",
            },
            {
              key: "delete",
              label: "Delete",
              icon: <DeleteOutlined />,
              onClick: () => deleteSelectedExportDocFee(record.id),
              disabled: !hasPermission([
                CERTIFICATE_OF_ORIGIN_FEE_DELETE_PERMISSION,
              ]),
              danger: true,
            },
          ];

          return (
            <Dropdown
              menu={{ items: menuItems }}
              placement="bottomRight"
              trigger={["hover"]}
            >
              <Button type="default" icon={<MoreOutlined />} />
            </Dropdown>
          );
        },
      },
    ],
    [deleteSelectedExportDocFee, navigateToEdit, navigateToView],
  );

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={getAllExportDocFees}
        filterName="certificate_of_origin_fee"
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        searchButtonText="Search"
      />
    ),
    [showFilter],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={navigateToAdd}
                disabled={
                  !hasPermission([CERTIFICATE_OF_ORIGIN_FEE_CREATE_PERMISSION])
                }
                addButtonText={
                  hasPermission([CERTIFICATE_OF_ORIGIN_FEE_CREATE_PERMISSION])
                    ? "Add New"
                    : "No Permission"
                }
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>Certificate of Origin Fee</Title>}
          />
        }
        content={
          <>
            {memoizedCustomFilter}

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => getAllExportDocFees(filterData)}
                dataSource={tableData}
                columns={tableColumns}
                rowKey="id"
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize || 10,
                  current: metaData?.current || 1,
                  onChange: changePage,
                  defaultCurrent: metaData?.defaultCurrent || 1,
                  total: metaData?.total || 0,
                  showTotal: (total) => `Total ${total} items`,
                }}
                bordered
                size="middle"
                scroll={{
                  x: "max-content",
                  y: tableData.length > 10 ? 500 : undefined,
                }}
                style={{ borderRadius: "8px", overflow: "hidden" }}
              />
            </div>
          </>
        }
      />
    </>
  );
}
